// «Назад» на телефоне (жест, кнопка Android, «Назад» Telegram) закрывает открытую шторку или окно, а не уходит со страницы.
// Открытие добавляет запись в историю с тем же адресом. Закрыли кнопкой («Готово», крестик) — запись помечается
// закрытой: «Назад» на неё или с неё пропускается, лишнего нажатия нет, прошлый поиск не повторяется.
// Обработчик popstate — в фазе перехвата: срабатывает раньше обработчиков разделов и останавливает их.
import { useEffect, useRef } from 'react';

type OverlayState = { sayohatOverlay?: number; done?: boolean } & Record<string, unknown>;

const closers: { id: number; close: () => void }[] = [];
let seq = 0;
/** закрытая кнопкой запись наверху истории: «Назад» с неё на тот же адрес — пропустить */
let dead: { len: number; href: string } | null = null;
/** сами ушли дальше «Назад» — следующий popstate обычный */
let skipping = false;
let installed = false;

const stateOf = (v: unknown): OverlayState | null => (v && typeof v === 'object' ? (v as OverlayState) : null);

function onPop(e: PopStateEvent): void {
  // Открыта шторка — закрываем её
  const top = closers.pop();
  if (top) { e.stopImmediatePropagation(); dead = null; top.close(); return; }
  // Пришли на запись уже закрытой шторки — она ничего не показывает, идём дальше
  const st = stateOf(e.state);
  if (st?.sayohatOverlay) { e.stopImmediatePropagation(); skipping = true; history.back(); return; }
  if (skipping) { skipping = false; dead = null; return; }
  // Ушли с закрытой шторки на тот же адрес (экран не изменился) — пользователь хотел назад, а не «на месте»
  if (dead) {
    const d = dead;
    dead = null;
    if (history.length === d.len && location.href === d.href) { e.stopImmediatePropagation(); skipping = true; history.back(); }
  }
}

/** Открыть шторку: запись в истории; ответ — функция «закрыли не через „Назад“» */
export function pushOverlay(close: () => void): () => void {
  if (!installed) { installed = true; window.addEventListener('popstate', onPop, { capture: true }); }
  const id = ++seq;
  dead = null;
  try { history.pushState({ ...(stateOf(history.state) || {}), sayohatOverlay: id }, '', location.href); } catch { return () => {}; }
  closers.push({ id, close });
  return () => {
    const i = closers.findIndex((c) => c.id === id);
    if (i < 0) return; // закрыли «Назад» — запись уже снята
    closers.splice(i, 1);
    const st = stateOf(history.state);
    if (st?.sayohatOverlay === id) {
      try { history.replaceState({ ...st, done: true }, '', location.href); } catch { /* нет истории */ }
      dead = { len: history.length, href: location.href };
    }
  };
}

const onPhone = (): boolean => {
  try {
    return matchMedia('(max-width: 760px)').matches || document.documentElement.classList.contains('tg-app');
  } catch {
    return false;
  }
};

/** Шторка или окно открыто на телефоне — «Назад» вызывает close */
export function useBackClose(open: boolean, close: () => void): void {
  const ref = useRef(close);
  ref.current = close;
  useEffect(() => {
    if (!open || !onPhone()) return undefined;
    return pushOverlay(() => ref.current());
  }, [open]);
}
